import { useRoleProfileActionsPermissions } from "PFApp/hooks/use_role_profile_actions_permissions";
import { ActionIcon } from "PFComponents/action_icon";
import { Typography } from "PFComponents/typography";
import { profileKeys } from "PFCore/hooks/queries/profile";
import { useShortlistReset } from "PFCore/hooks/queries/shortlists/use_shortlist_actions";
import { useShortlistDelete } from "PFCore/hooks/queries/shortlists/use_shortlist_delete";
import { Activity, Profile, Shortlist } from "PFTypes";
import { useTranslation } from "react-i18next";

import css from "./shortlist_buttons.module.scss";

type ShortlistStateLabelProps = {
  role: Activity;
  shortlist: Pick<Shortlist, "id" | "state"> | null;
  profile: Pick<Profile, "id"> & Partial<Profile>;
  isUndoDisabled?: boolean;
  onUndoSuccess?: () => void;
  onShortlistDeleteSuccess?: (profileId: number) => void;
  isOnMatchesTab?: boolean;
};

export const ShortlistStateLabel = ({
  role,
  shortlist,
  profile,
  isUndoDisabled,
  onUndoSuccess,
  onShortlistDeleteSuccess,
  isOnMatchesTab = false
}: ShortlistStateLabelProps) => {
  const { t } = useTranslation("activities");

  const { checkCanUndo } = useRoleProfileActionsPermissions();

  const isPending = shortlist?.state === "pending";
  const isUndoAllowedOnCurrentPage = isPending || !isOnMatchesTab;
  const displayUndoButton = checkCanUndo(role, profile, shortlist) && isUndoAllowedOnCurrentPage;

  const { mutateAsync: deleteShortlist, isPending: isDeleting } = useShortlistDelete({
    activityId: role.id,
    options: {
      onSuccess: () => {
        onUndoSuccess?.();
      }
    }
  });
  const { mutate: resetShortlist, isPending: isResetting } = useShortlistReset(
    {
      onSuccess: () => onUndoSuccess?.()
    },
    { queryKeys: [profileKeys.all()] }
  );

  const handleUndoClick = () => {
    if (shortlist!.state === "pending") {
      deleteShortlist(shortlist!.id).then(() => {
        onShortlistDeleteSuccess?.(profile.id);
      });
    } else {
      resetShortlist(shortlist!.id);
    }
  };

  const labelText = t(`show.shortlist.state.${shortlist!.state}`);

  const hasException = role.workflow_state === "exception";
  const isDisabled = hasException || isUndoDisabled || isDeleting || isResetting;

  return (
    <div className={css.labelWrapper}>
      <Typography withMargin variant="labelRegular">
        {labelText}
      </Typography>
      {displayUndoButton && (
        <ActionIcon
          name="history"
          size="sm"
          disabled={isDisabled}
          onClick={(event) => {
            event.stopPropagation();
            handleUndoClick();
          }}
        />
      )}
    </div>
  );
};
